import { adminApiClient } from './adminApiClient';
import type { ErpRegistry } from './erpRegistriesService';
import type { ErpEndpoint } from './erpEndpointsService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ErpRequestLog {
  id: string;
  trace_id: string;
  tenant_id: string | null;
  erp_registry_id: string;
  erp_endpoint_id: string;
  request_method: string;
  request_url: string;
  request_headers: Record<string, unknown> | null;
  request_body: Record<string, unknown> | null;
  response_status: number | null;
  response_headers: Record<string, unknown> | null;
  response_body: Record<string, unknown> | null;
  transformed_response: Record<string, unknown> | null;
  error_code: string | null;
  error_message: string | null;
  duration_ms: number;
  retry_count: number;
  created_at: string;
  updated_at: string;
  erp_registry: ErpRegistry | null;
  erp_endpoint: ErpEndpoint | null;
}

export interface ErpRequestLogListParams {
  erp_registry_id?: string;
  erp_endpoint_id?: string;
  status?: 'success' | 'error';
  response_status?: number;
  error_code?: string;
  trace_id?: string;
  from?: string;
  to?: string;
  min_duration_ms?: number;
  sort?: 'created_at' | '-created_at' | 'duration_ms' | '-duration_ms';
  per_page?: number;
  page?: number;
}

/**
 * Laravel length-aware paginator metadata returned by `GET /admin/erp/request-logs`.
 * Extra keys (`path`, `links`, ...) are kept loose.
 */
export interface ErpRequestLogListMeta extends Record<string, unknown> {
  current_page: number;
  from: number | null;
  last_page: number;
  per_page: number;
  to: number | null;
  total: number;
}

export interface ErpRequestLogListResponse {
  data: ErpRequestLog[];
  meta: ErpRequestLogListMeta;
  links: Record<string, unknown>;
}

export interface ErpRequestLogStatsParams {
  erp_registry_id?: string;
  erp_endpoint_id?: string;
  from?: string;
  to?: string;
  group_by?: 'erp_registry_id' | 'erp_endpoint_id' | 'hour' | 'day';
}

export interface ErpRequestLogTopError {
  error_code: string;
  count: number;
}

export interface ErpRequestLogStats {
  total: number;
  success_count: number;
  error_count: number;
  success_rate: number;
  avg_duration_ms: number;
  max_duration_ms: number;
  total_retries: number;
  top_errors: ErpRequestLogTopError[];
  groups?: Record<string, unknown>[];
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const basePath = '/admin/erp/request-logs';

export const erpRequestLogsService = {
  async list(params?: ErpRequestLogListParams): Promise<ErpRequestLogListResponse> {
    const response = await adminApiClient.get<ErpRequestLogListResponse>(basePath, { params });
    return response.data;
  },

  async get(id: string): Promise<{ data: ErpRequestLog }> {
    const response = await adminApiClient.get<{ data: ErpRequestLog }>(
      `${basePath}/${id}`,
    );
    return response.data;
  },

  async getByTraceId(traceId: string): Promise<{ data: ErpRequestLog[] }> {
    const response = await adminApiClient.get<{ data: ErpRequestLog[] }>(
      `${basePath}/trace/${traceId}`,
    );
    return response.data;
  },

  async getStats(
    params?: ErpRequestLogStatsParams,
  ): Promise<{ data: ErpRequestLogStats }> {
    const response = await adminApiClient.get<{ data: ErpRequestLogStats }>(
      `${basePath}/stats`,
      { params },
    );
    return response.data;
  },
};
